// Story 7.10 (ADO-906) — attribute-facet wire shapes + query serialisation shared
// by the browse and search read clients (FR14/FR104). Hand-duplicated from the
// backend `search-and-discovery` FacetDto contract until the openapi-typescript
// generated client lands (7.1 D-6 posture) — keep the two in lock-step.

/** The filterable attributes (FR104). camelCase wire keys, matching the backend
 *  FacetKey (`facet.dto.ts` says what each reads). `brand`, `packSize` and
 *  `countryOfOrigin` are value facets; `newListing`, `ownBrand`, `mixedCase` and
 *  `smallCase` are flag facets whose one value is `true` (ADO-3692);
 *  `promotionalStatus` + `offerType` are offer facets that yield no values yet,
 *  and so never render. */
export type FacetKey =
  | 'brand'
  | 'packSize'
  | 'promotionalStatus'
  | 'offerType'
  | 'newListing'
  | 'ownBrand'
  | 'mixedCase'
  | 'smallCase'
  | 'countryOfOrigin';

/** All facet keys in display order — the single source the panel drives off. */
export const FACET_KEYS: readonly FacetKey[] = [
  'brand',
  'packSize',
  'promotionalStatus',
  'offerType',
  'newListing',
  'ownBrand',
  'mixedCase',
  'smallCase',
  'countryOfOrigin',
];

/** Human labels for each facet section (Figma FilterPanel section headings). */
export const FACET_LABELS: Record<FacetKey, string> = {
  brand: 'Brand',
  packSize: 'Product Size',
  promotionalStatus: 'Show Me',
  offerType: 'Deals & Listing Type',
  newListing: 'Deals & Listing Type',
  ownBrand: 'Deals & Listing Type',
  mixedCase: 'Deals & Listing Type',
  smallCase: 'Deals & Listing Type',
  countryOfOrigin: 'Country of Origin',
};

/**
 * The rail checkboxes wired to a facet value, by the label the rail and its chip show:
 * - the offer toggles, each by the server's offer kind (Enabler E7-C, ADO-2274: the facets'
 *   values are the MP OfferKind names, search's `listing-filters.ts` OFFER_KINDS_BY_FACET). The
 *   facets' other kinds (`LEAFLET_LINE`, `WOW`, `RBP`) have no toggle yet.
 * - the listing toggles, each by its flag facet's one value `true` (ADO-3692). New Lines and New
 *   Listings drive the same filter (D-6); New Listings comes first, so the chip shows that name.
 */
export const FACET_TOGGLES: Readonly<Record<string, { key: FacetKey; value: string }>> = {
  'On Offer': { key: 'promotionalStatus', value: 'PROMOTION' },
  'Multi Buy': { key: 'offerType', value: 'MULTI_BUY' },
  'New Listings': { key: 'newListing', value: 'true' },
  'New Lines': { key: 'newListing', value: 'true' },
  'Own Brand': { key: 'ownBrand', value: 'true' },
  'Mixed Case': { key: 'mixedCase', value: 'true' },
  'Small Case': { key: 'smallCase', value: 'true' },
};

/** The label a selected facet value shows on its chip: a toggle's label, else the value. */
export function facetValueLabel(key: FacetKey, value: string): string {
  const toggle = Object.entries(FACET_TOGGLES).find(
    ([, mapping]) => mapping.key === key && mapping.value === value,
  );
  return toggle?.[0] ?? value;
}

/** One available value within a facet + its match count in the current set. */
export interface FacetValue {
  readonly value: string;
  readonly count: number;
}

/** A facet: its key + the available values (with counts). Zero-count values are
 *  never present; a facet with no values is omitted by the server (FR104). */
export interface Facet {
  readonly key: FacetKey;
  readonly values: readonly FacetValue[];
}

/** The active attribute filters: facet key → selected values. AND across facets,
 *  OR within a facet's values (D-4). An absent/empty key imposes no constraint. */
export type ProductFilters = Partial<Record<FacetKey, readonly string[]>>;

/** Append the active facet filters to a URLSearchParams as comma-separated
 *  camelCase params (`?brand=Kelloggs,Nestle`), matching the backend validator's
 *  accepted wire format. Empty selections are skipped. */
export function appendFilterParams(params: URLSearchParams, filters: ProductFilters): void {
  for (const key of FACET_KEYS) {
    const values = filters[key];
    if (values !== undefined && values.length > 0) {
      params.set(key, values.join(','));
    }
  }
}

/** Whether any facet has at least one selected value. */
export function hasActiveFilters(filters: ProductFilters): boolean {
  return FACET_KEYS.some((k) => (filters[k]?.length ?? 0) > 0);
}

/** Toggle a single value within a facet (add if absent, remove if present),
 *  returning a NEW filters object (never mutates). Removing the last value drops
 *  the key entirely so `hasActiveFilters` stays accurate. */
export function toggleFilterValue(
  filters: ProductFilters,
  key: FacetKey,
  value: string,
): ProductFilters {
  const current = filters[key] ?? [];
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  const out: ProductFilters = { ...filters };
  if (next.length === 0) delete out[key];
  else out[key] = next;
  return out;
}

/** Remove one value from a facet (chip dismissal) — same as toggle when present. */
export function removeFilterValue(
  filters: ProductFilters,
  key: FacetKey,
  value: string,
): ProductFilters {
  if (!(filters[key]?.includes(value) ?? false)) return filters;
  return toggleFilterValue(filters, key, value);
}

/** The flat list of active (key, value) pairs, for rendering removable chips. */
export function activeFilterChips(
  filters: ProductFilters,
): ReadonlyArray<{ key: FacetKey; value: string }> {
  const chips: Array<{ key: FacetKey; value: string }> = [];
  for (const key of FACET_KEYS) {
    for (const value of filters[key] ?? []) chips.push({ key, value });
  }
  return chips;
}
